/**
 * Produktserien-Modals der Hauptnavigation.
 *
 * Die Navigations-Kindknoten unter "Produktserien" zeigen auf die Detail-URL
 * des jeweiligen Kollektionseintrags, mit dem Marker-Hash '#produktserie':
 *
 *     /de/alferstyle#produktserie
 *
 * Dieses Modul faengt solche Klicks ab, laedt GENAU DIESE eine Detailseite per
 * fetch nach, schneidet den Container .js-produktserienModalContent heraus
 * (gerendert von templates/collections/collection_produktserien_full.php) und
 * zeigt ihn in einem <dialog>.
 *
 * Warum so:
 *   - Auf normalen Seiten liegt KEIN Modal-Markup im DOM. Der Dialog wird erst
 *     beim ersten Klick erzeugt, die Inhalte kommen pro Serie bei Bedarf.
 *   - Der Hash erreicht den Server nie, ist also ein reiner JS-Marker. jumpmark.js
 *     aus dem Basistheme fasst ihn nicht an, weil es nur Links betrachtet, deren
 *     href MIT '#' beginnt - hier steht davor ein Pfad. Kein Konflikt.
 *   - Ohne JS, bei Mittel-/Rechtsklick oder wenn der fetch scheitert, oeffnet
 *     sich die echte Seite. Progressive Enhancement, kein Sackgassen-Modal.
 *
 * Browser-Zurueck schliesst das Modal (pushState + popstate), ein Reload landet
 * auf der echten Seite.
 *
 * Kein Core-Modal (.modal/data-modal), sondern natives <dialog>: Focus-Trap,
 * Escape und Top-Layer kommen dann vom Browser - dasselbe Muster wie
 * productHotspots.js.
 *
 * Vanilla JS, kein jQuery.
 */

const MARKER_HASH = '#produktserie';
const CONTENT_SELECTOR = '.js-produktserienModalContent';

// Gerenderte Inhalte je Pfad, damit ein zweiter Klick auf dieselbe Serie
// keinen weiteren Request ausloest.
const cache = new Map();

let dialog = null;
let body = null;
let closeBtn = null;
let originalUrl = '';
let currentPath = '';
// Waehrend wir selbst durch die History navigieren, darf das close-Event die
// URL nicht noch ein zweites Mal zurechtruecken.
let restoringHistory = false;

function buildDialog() {
    if (dialog) { return dialog; }

    dialog = document.createElement('dialog');
    dialog.className = 'produktserienModal js-produktserienModal';
    dialog.setAttribute('aria-label', 'Produktserie');
    dialog.innerHTML =
        '<div class="produktserienModal__content js-produktserienModalBody"></div>' +
        '<button type="button" class="produktserienModal__close js-produktserienModalClose" aria-label="Schließen">' +
        '<i class="icon icon-close" aria-hidden="true"></i></button>';

    body = dialog.querySelector('.js-produktserienModalBody');
    closeBtn = dialog.querySelector('.js-produktserienModalClose');

    closeBtn.addEventListener('click', () => dialog.close());
    // Klick auf den Backdrop: das Event-Target ist dann der Dialog selbst.
    dialog.addEventListener('click', (event) => {
        if (event.target === dialog) { dialog.close(); }
    });
    // Alles Schliessen laeuft hier zusammen - auch Escape, das der Browser
    // selbst behandelt. Deshalb haengt die URL-Wiederherstellung am close-Event
    // und nicht an den Buttons: sonst bliebe die Serien-URL nach Escape stehen.
    dialog.addEventListener('close', () => {
        currentPath = '';
        dialog.classList.remove('is-loading');
        if (!restoringHistory) { restoreUrl(); }
    });

    document.body.appendChild(dialog);
    return dialog;
}

function setLoading() {
    dialog.classList.add('is-loading');
    body.innerHTML = '<div class="produktserienModal__loading" aria-live="polite">' +
        '<i class="icon icon-loading" aria-hidden="true"></i></div>';
}

function setContent(html) {
    dialog.classList.remove('is-loading');
    body.innerHTML = html;
    if (closeBtn) { closeBtn.focus(); }
}

// Detailseite holen und den Marker-Container extrahieren.
async function loadContent(path) {
    if (cache.has(path)) { return cache.get(path); }

    const response = await fetch(path, { credentials: 'same-origin' });
    if (!response.ok) { throw new Error('HTTP ' + response.status); }

    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const content = parsed.querySelector(CONTENT_SELECTOR);
    if (!content) { throw new Error('Marker ' + CONTENT_SELECTOR + ' nicht gefunden'); }

    const html = content.outerHTML;
    cache.set(path, html);
    return html;
}

// Ein offenes Mobile-Menue wuerde ueber dem Dialog liegen bleiben. Ueber den
// vorhandenen Close-Button schliessen, statt in mobileMenu.js einzugreifen.
function closeMobileMenu() {
    if (!document.body.classList.contains('is-mobilemenuopen')) { return; }
    const trigger = document.querySelector('.mobileMenu__close');
    if (trigger) { trigger.click(); }
}

async function open(path, fallbackUrl) {
    closeMobileMenu();
    buildDialog();

    if (!dialog.open) { dialog.showModal(); }

    currentPath = path;
    pushUrl(path);
    setLoading();

    try {
        const html = await loadContent(path);
        // Zwischenzeitlich wurde eine andere Serie angefordert oder geschlossen.
        if (currentPath !== path) { return; }
        setContent(html);
    } catch {
        // Ehrlicher Fallback: die echte Seite, statt ein leeres Modal.
        window.location.href = fallbackUrl || path;
    }
}

function pushUrl(path) {
    const state = { produktserie: path };
    if (window.history.state && window.history.state.produktserie) {
        window.history.replaceState(state, '', path);
    } else {
        window.history.pushState(state, '', path);
    }
}

// Nach dem Schliessen soll die URL wieder zur Ausgangsseite passen. Ueber
// history.back(), damit der eigene History-Eintrag verschwindet statt sich zu
// stapeln.
function restoreUrl() {
    if (window.history.state && window.history.state.produktserie) {
        window.history.back();
        return;
    }
    if (originalUrl) { window.history.replaceState(null, '', originalUrl); }
}

function isModalLink(link) {
    if (!link || link.origin !== window.location.origin) { return false; }
    if ((link.hash || '').toLowerCase() !== MARKER_HASH) { return false; }
    return link.target !== '_blank';
}

export function initProduktserienModal() {
    // Ausgangs-URL einmalig festhalten: nach einem popstate zeigt
    // window.location schon auf die Serien-URL und waere als Rueckfallziel falsch.
    originalUrl = window.location.pathname + window.location.search;

    // Kein Early-Return auf ein Root-Element: die Navigation steht auf jeder
    // Seite, der Dialog entsteht erst beim ersten Treffer.
    document.addEventListener('click', (event) => {
        // Modifizierte Klicks (neuer Tab/Fenster, Download) nicht abfangen.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) { return; }

        const target = event.target;
        const link = (target && target.nodeType === 1) ? target.closest('a[href]') : null;
        if (!isModalLink(link)) { return; }

        event.preventDefault();
        event.stopPropagation();
        open(link.pathname + link.search, link.href);
    }, true);

    window.addEventListener('popstate', (event) => {
        const state = event.state;
        if (state && state.produktserie) {
            open(state.produktserie, state.produktserie);
            return;
        }
        currentPath = '';
        if (dialog && dialog.open) {
            restoringHistory = true;
            dialog.close();
            restoringHistory = false;
        }
    });
}
